/**
 * A barra que fica na sidebar enquanto se está numa chamada e se anda a ler
 * outro canal. Sem ela, sair de uma chamada obriga a procurar o canal onde se
 * entrou — o erro que faz as pessoas ficarem em salas horas depois de sairem.
 */

import { ConnectionQuality } from "livekit-client";

import { useVoiceActions, useVoiceSnapshot, voiceLive, voiceProblem } from "../hooks/useVoice";
import { screenShareSupported } from "../lib/media";
import { voiceEngine } from "../lib/voice";
import { useStore } from "../store/store";
import { IconClose, IconLock, IconPhoneOff, IconScreen, IconSpeaker } from "./Icons";
import { SignalIcon } from "./SignalIcon";

const LABEL: Record<string, string> = {
  idle: "Preparando…",
  connecting: "Conectando…",
  connected: "Conectado",
  reconnecting: "Recuperando…",
  failed: "Falhou",
};

export function VoiceBar() {
  const state = useStore();
  const snapshot = useVoiceSnapshot();
  const actions = useVoiceActions();

  const session = state.voice;
  if (!session) return null;

  const channel = state.channels[session.channelId];
  const server = session.serverId ? state.servers[session.serverId] : undefined;
  // `voiceLive` e não `=== "connected"`: a recuperar a ligação ainda é uma
  // chamada, e era esta linha que matava o botão do microfone precisamente no
  // instante em que mais se quer fechá-lo.
  const live = voiceLive(snapshot);
  const problem = voiceProblem(snapshot, session.error);
  // O mesmo par de donos da permissão do palco: o cargo E o aparelho.
  const canCaptureScreen = screenShareSupported();
  // A minha ligação, para o mostrador. Enquanto a call não arranca não há
  // participante local nenhum: as barras ficam apagadas e o título diz que se
  // está a medir, que é a verdade.
  const eu = snapshot.participants.find((p) => p.isLocal);

  return (
    <div className={`voice-bar${live ? " is-live" : ""}`}>
      <PrivateVoiceStrip />
      {/* Fora do botão de propósito: o `title` de um filho ganha ao do pai, e
          dentro dele o mostrador roubava o «Ir para a sala» a metade da
          largura da barra. Aqui cada um responde pela sua área. */}
      <span className="voice-bar__net">
        <SignalIcon quality={eu?.quality ?? ConnectionQuality.Unknown} identity={eu?.identity ?? null} size={16} />
      </span>
      <button
        type="button"
        className="voice-bar__where"
        onClick={() => state.selectChannel(session.channelId)}
        title="Ir para a sala"
      >
        <IconSpeaker size={16} />
        <span className="voice-bar__names">
          <strong>{LABEL[snapshot.state] ?? snapshot.state}</strong>
          {/* Havendo problema, ele ocupa a linha de baixo em vez do «sala ·
              servidor». Não é falta de espaço para os dois: quem carregou no
              microfone e não viu nada acontecer precisa do motivo, e já sabe
              em que sala está — foi ele que entrou. O palco tem o seu próprio
              aviso; esta barra é a única coisa à vista quando se está a ler
              outro canal, e sem isto o erro não chegava a lado nenhum. */}
          <small className={problem ? "voice-bar__problem" : undefined} title={problem ?? undefined}>
            {problem ?? `${channel?.name ?? "sala"}${server ? ` · ${server.name}` : ""}`}
          </small>
        </span>
      </button>

      {/* Aqui NÃO há microfone nem auscultadores — vivem no rodapé, a três
          pixels daqui.

          Esta barra teve os dois, com a justificação de que era «a única coisa
          sempre no ecrã durante uma chamada, e no telemóvel o rodapé nem sempre
          está à vista». Não era verdade: barra e rodapé são irmãos dentro do
          mesmo `.shell__sidebar` (`AppShell`, `OfficeSpace`), sem media query
          que esconda um deles — aparecem e desaparecem juntos, no telemóvel
          como no computador. O resultado do engano era o microfone fechado
          desenhado TRÊS vezes na mesma coluna: aqui, no selo ao pé do meu nome,
          e no botão do rodapé.

          O rodapé ficou com eles porque está lá SEMPRE, em chamada ou fora
          dela, no mesmo pixel. Esta barra vai-se embora quando a chamada acaba,
          e um controlo que muda de sítio conforme o estado é o contrário de
          memória muscular. Fica-lhe o que só ela pode ter: onde estou,
          partilhar o ecrã, e sair. */}
      <div className="voice-bar__actions">
        <button
          type="button"
          // Vermelho a partilhar, como o do palco: é o mesmo botão e a mesma
          // pergunta — o que acontece ao carregar é CORTAR o que os outros
          // estão a ver, e isso não se pinta de verde.
          className={`icon-button${snapshot.screenEnabled ? " is-stop" : ""}`}
          disabled={!live || session.capabilities.canShareScreen === false || !canCaptureScreen}
          onClick={() => void actions.toggleScreen()}
          title={
            canCaptureScreen
              ? snapshot.screenEnabled
                ? "Parar compartilhamento"
                : "Compartilhar tela"
              : "Este aparelho não permite partilhar o ecrã"
          }
        >
          <IconScreen size={18} />
        </button>
        <button
          type="button"
          className="icon-button icon-button--danger"
          onClick={() => void actions.leave()}
          title="Sair da chamada"
        >
          <IconPhoneOff size={18} />
        </button>
      </div>
    </div>
  );
}

/**
 * O aviso de que o microfone NÃO está a ir para a sala.
 *
 * Isto não é enfeite: a voz privada é o único estado da app em que se fala e a
 * sala não ouve, e um estado invisível desses acaba sempre da mesma maneira —
 * alguém a explicar uma coisa durante um minuto para uma pessoa só, convencido
 * de que estava a falar para todos. Por isso vive na barra da chamada, que está
 * SEMPRE no ecrã enquanto durar a chamada (o cartão de perfil onde se liga
 * fecha-se logo a seguir), e traz o botão de desligar ao lado do aviso: quem lê
 * "só o Xavier me ouve" tem de poder corrigir ali, não ir procurar a pessoa.
 *
 * O lado de quem RECEBE aparece pela mesma razão ao contrário: ouvir alguém sem
 * saber que mais ninguém o ouve leva a responder para o grupo sobre uma coisa
 * que o grupo não ouviu.
 */
function PrivateVoiceStrip() {
  const snapshot = useVoiceSnapshot();
  const users = useStore((s) => s.users);

  const name = (id: string) => users[id]?.displayName ?? users[id]?.username ?? "alguém";
  const to = snapshot.privateTo;
  const from = snapshot.privateFrom;
  if (!to && from.length === 0) return null;

  return (
    <div className="voice-bar__private">
      {to ? (
        <span className="voice-bar__private-row">
          <IconLock size={14} />
          <span>
            Só <strong>{name(to)}</strong> te ouve
          </span>
          <button
            type="button"
            className="icon-button"
            title="Voltar a falar para a sala"
            onClick={() => void voiceEngine.setPrivateVoice(null)}
          >
            <IconClose size={14} />
          </button>
        </span>
      ) : null}
      {from.map((id) => (
        <span className="voice-bar__private-row" key={id}>
          <IconLock size={14} />
          <span>
            <strong>{name(id)}</strong> fala só para ti
          </span>
        </span>
      ))}
    </div>
  );
}
